import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import {
  AgentProfileProperties2026TabFragment,
  ListingFilterStatus,
} from '../../../../generated/graphql';

export type PropertyCardListing = NonNullable<
  NonNullable<
    NonNullable<AgentProfileProperties2026TabFragment['results']>[number]
  >['listing']
>;

type PropertyStatus = ListingFilterStatus;

const formatDate = (
  value?: string | null,
  pattern = 'd MMM yyyy',
): string | undefined => {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  return isValid(date) ? format(date, pattern) : undefined;
};

const STATE_TIME_ZONES: Record<string, string> = {
  NSW: 'Australia/Sydney',
  ACT: 'Australia/Sydney',
  VIC: 'Australia/Melbourne',
  TAS: 'Australia/Hobart',
  QLD: 'Australia/Brisbane',
  SA: 'Australia/Adelaide',
  NT: 'Australia/Darwin',
  WA: 'Australia/Perth',
};
const DEFAULT_TIME_ZONE = 'Australia/Sydney';
const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

const formatInspectionDay = (
  inspection: Date,
  now: Date,
  timeZone: string,
): string => {
  const days = differenceInCalendarDays(
    utcToZonedTime(inspection, timeZone),
    utcToZonedTime(now, timeZone),
  );
  if (days === 0) {
    return 'today';
  }
  if (days === 1) {
    return 'tomorrow';
  }
  return formatInTimeZone(inspection, timeZone, 'EEE d MMM');
};

const formatInspectionTime = (inspection: Date, timeZone: string): string =>
  formatInTimeZone(
    inspection,
    timeZone,
    utcToZonedTime(inspection, timeZone).getMinutes() ? 'h:mmaaa' : 'haaa',
  );

const parseInspection = (value: string, timeZone: string): Date | undefined => {
  try {
    const date = HAS_OFFSET.test(value)
      ? parseISO(value)
      : zonedTimeToUtc(value, timeZone);
    return isValid(date) ? date : undefined;
  } catch {
    return undefined;
  }
};

const nextInspection = (
  listing: PropertyCardListing,
  now: Date,
  timeZone: string,
): Date | undefined =>
  (listing.inspectionDetails?.inspections ?? [])
    .map((inspection) =>
      inspection?.openingDateTime
        ? parseInspection(inspection.openingDateTime, timeZone)
        : undefined,
    )
    .filter((time): time is Date => !!time && time >= now)
    .sort((a, b) => a.getTime() - b.getTime())[0];

export const formatPropertyType = (type?: string | null): string =>
  type
    ? type
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : '';

export const getStatusBadge = (
  listing: PropertyCardListing,
  status: PropertyStatus,
  now: Date = new Date(),
): { label: string; family: 'accentFive' | 'accentOne' } | undefined => {
  if (status === ListingFilterStatus.Sold) {
    const soldDate = formatDate(listing.soldInfo?.soldDate);
    const soldMethod = listing.soldInfo?.soldMethod?.toLowerCase();
    let soldLabel = 'Sold';
    if (soldMethod === 'auction') {
      soldLabel = 'Sold at auction';
    } else if (soldMethod === 'soldpriortoauction') {
      soldLabel = 'Sold prior to auction';
    }

    return soldDate
      ? { label: `${soldLabel} ${soldDate}`, family: 'accentFive' }
      : undefined;
  }

  if (status === ListingFilterStatus.Leased) {
    const leasedDate = formatDate(
      listing.leasedInfo?.leasedDate,
      'd MMMM yyyy',
    );
    return leasedDate
      ? { label: `Leased ${leasedDate}`, family: 'accentFive' }
      : undefined;
  }

  const timeZone =
    STATE_TIME_ZONES[listing.address?.state?.toUpperCase() ?? ''] ??
    DEFAULT_TIME_ZONE;
  const inspection = nextInspection(listing, now, timeZone);

  if (!inspection) {
    return undefined;
  }

  return {
    label: `Inspect ${formatInspectionDay(inspection, now, timeZone)} ${formatInspectionTime(inspection, timeZone)}`,
    family: 'accentOne',
  };
};

const WEEKLY_SUFFIX = /\/?(?:p\.?w\.?|per week)$/i;

const toWeeklyPrice = (price: string): string => {
  const trimmed = price.trimEnd();
  const suffix = WEEKLY_SUFFIX.exec(trimmed);
  return suffix
    ? `${trimmed.slice(0, suffix.index).trimEnd()} per week`
    : price;
};

const NO_PRICE = '-';

export const formatPrice = (
  listing: PropertyCardListing,
  status: PropertyStatus,
): string => {
  if (status === ListingFilterStatus.Sold && listing.soldInfo?.soldPrice) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      maximumFractionDigits: 0,
    }).format(listing.soldInfo.soldPrice);
  }

  const price = (
    listing.priceDetails?.displayPrice?.replace(/^SOLD\s*-\s*/i, '') ?? ''
  ).trim();

  if (!price || /^sold$/i.test(price)) {
    return NO_PRICE;
  }

  return status === ListingFilterStatus.Lease ||
    status === ListingFilterStatus.Leased
    ? toWeeklyPrice(price)
    : price;
};
